import { Box, useTheme } from '@mui/material';
import { AccordionHeaderProps } from './AccordionHeader.props';
import { Typography, Chip, Avatar } from '@components/atoms';

/**
 * AccordionHeader component provides a flexible container for accordion header content.
 * Uses max-content width to only take up the space it needs.
 *
 * The component supports two usage patterns:
 * 1. **Custom Layout (children)**: Complete control over layout and positioning
 * 2. **Predefined Layouts**: Convenient props for common patterns
 *
 * @example
 * // Custom layout with complete control
 * <AccordionHeader>
 *   <Typography>Title</Typography>
 *   <Chip label={5} />
 * </AccordionHeader>
 *
 * @example
 * // Predefined layout: title-count
 * <AccordionHeader
 *   layout="title-count"
 *   title="Documents"
 *   count={5}
 * />
 *
 * @example
 * // Predefined layout: count-title (reversed)
 * <AccordionHeader
 *   layout="count-title"
 *   title="Important Files"
 *   count={3}
 * />
 *
 * @example
 * // Predefined layout: avatar-title
 * <AccordionHeader
 *   layout="avatar-title"
 *   title="John Doe"
 *   avatar={{ name: "John Doe", src: "/avatar.jpg" }}
 * />
 *
 * @example
 * // Predefined layout: title-avatar (reversed)
 * <AccordionHeader
 *   layout="title-avatar"
 *   title="John Doe"
 *   avatar={{ name: "John Doe" }}
 * />
 */
export const AccordionHeader = ({
  children,
  className,
  layout = 'custom',
  title,
  titleProps,
  count,
  countProps,
  avatar,
  avatarProps,
  gap = 1,
  ...props
}: AccordionHeaderProps) => {
  const theme = useTheme();

  // If children are provided, use custom layout
  if (children) {
    return (
      <Box
        className={`accordion-header accordion-header--custom ${className || ''}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 'max-content',
          gap,
          ...props.sx,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  // Render title component
  const titleComponent = title ? (
    <Typography
      sx={{ ...theme.typography.M3.body.large, ...titleProps?.sx }}
      {...titleProps}
    >
      {title}
    </Typography>
  ) : null;

  // Render count component
  const countComponent =
    count !== undefined && count !== null ? (
      <Chip label={count} size="s" {...countProps} />
    ) : null;

  // Render avatar component
  const avatarComponent = avatar ? (
    <Avatar
      src={avatar.src}
      alt={avatar.alt}
      name={avatar.name}
      sx={{ width: 32, height: 32, ...avatarProps?.sx }}
      {...avatarProps}
    >
      {avatar.children}
    </Avatar>
  ) : null;

  // Render based on layout
  const renderContent = () => {
    switch (layout) {
      case 'title-count':
        return (
          <>
            {titleComponent}
            {countComponent}
          </>
        );
      case 'count-title':
        return (
          <>
            {countComponent}
            {titleComponent}
          </>
        );
      case 'title-avatar':
        return (
          <>
            {titleComponent}
            {avatarComponent}
          </>
        );
      case 'avatar-title':
        return (
          <>
            {avatarComponent}
            {titleComponent}
          </>
        );
      default:
        // For custom layout without children, render all available components
        return (
          <>
            {avatarComponent}
            {titleComponent}
            {countComponent}
          </>
        );
    }
  };

  return (
    <Box
      className={`accordion-header accordion-header--${layout} ${className || ''}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: 'max-content',
        gap,
        ...props.sx,
        paddingRight: theme.spacing(3),
      }}
      {...props}
    >
      {renderContent()}
    </Box>
  );
};

export default AccordionHeader;
